import { applyPatches, enablePatches, type Patch, produceWithPatches } from 'immer';
import { isEqual } from 'radash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { PersistedItem } from '../types/DataModel';

interface UseUndoRedoReturn {
    undo: (e?: MouseEvent) => void;
    redo: (e?: MouseEvent) => void;
    canUndo: boolean;
    canRedo: boolean;
}

// Coalesce rapid edits (e.g. typing) into a single undo step.
const SNAPSHOT_DEBOUNCE_MS = 400;
// Bound history.
const MAX_STACK_SIZE = 100;

interface HistoryEntry {
    redo: Patch[];
    undo: Patch[];
}

enablePatches();

// RHF's `getValues()` returns a SHALLOW merge of defaults and current values
// (`{...b, ...x}`); nested objects share references with its internal
// `_formValues`, which RHF mutates in place on every `setValue`. We must take
// a deep clone before stashing the value as our diff baseline, otherwise the
// next setValue would mutate the snapshot and the diff would see prev===next.
const isDate = (value: unknown): value is Date => value instanceof Date;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value) && !isDate(value);

// Arrays and plain objects are walked. A Date is a leaf: it has no keys, so
// walking it looks like an empty object and the edit is dropped.
const isNestedValue = (value: unknown): value is Record<string, unknown> | unknown[] =>
    Array.isArray(value) || isPlainObject(value);

// Two Date instances for the same moment are not ===. Treating them as a
// change records a no-op undo step and the real edit is never reversed.
const sameSnapshotValue = (left: unknown, right: unknown): boolean => {
    if (left === right) return true;
    if (isDate(left) && isDate(right)) return left.getTime() === right.getTime();
    return false;
};

const cloneSnapshot = <T,>(value: T): T => {
    if (value === null || typeof value !== 'object') return value;
    // Date has no enumerable keys; copying it as a plain object yields {}.
    if (isDate(value)) return new Date(value.getTime()) as T;
    if (Array.isArray(value)) return value.map(cloneSnapshot) as unknown as T;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>)) {
        out[key] = cloneSnapshot((value as Record<string, unknown>)[key]);
    }
    return out as T;
};

// Mutate `draft` so that it deep-equals `next`, walking only into subtrees that
// actually differ. Doing it this way lets Immer record minimal patches (one
// per primitive that changed) instead of a single top-level "replace" that
// would store the whole document.
const reconcile = (draft: unknown, next: unknown): void => {
    if (draft === next) return;

    if (
        typeof draft !== 'object' || draft === null ||
        typeof next !== 'object' || next === null ||
        Array.isArray(draft) !== Array.isArray(next)
    ) {
        // Caller must replace at the parent level; leaves are handled there.
        return;
    }

    if (Array.isArray(draft) && Array.isArray(next)) {
        const draftArr = draft as unknown[];
        const nextArr = next as unknown[];
        if (draftArr.length !== nextArr.length) {
            draftArr.length = nextArr.length;
        }
        for (let i = 0; i < nextArr.length; i++) {
            const dv = draftArr[i];
            const nv = nextArr[i];
            if (sameSnapshotValue(dv, nv)) continue;
            if (isNestedValue(dv) && isNestedValue(nv) && Array.isArray(dv) === Array.isArray(nv)) {
                reconcile(dv, nv);
            } else {
                draftArr[i] = nv;
            }
        }
        return;
    }

    const draftObj = draft as Record<string, unknown>;
    const nextObj = next as Record<string, unknown>;

    for (const key of Object.keys(nextObj)) {
        const dv = draftObj[key];
        const nv = nextObj[key];
        if (sameSnapshotValue(dv, nv)) continue;
        if (isNestedValue(dv) && isNestedValue(nv) && Array.isArray(dv) === Array.isArray(nv)) {
            reconcile(dv, nv);
        } else {
            draftObj[key] = nv;
        }
    }
    for (const key of Object.keys(draftObj)) {
        if (!Object.prototype.hasOwnProperty.call(nextObj, key)) {
            delete draftObj[key];
        }
    }
};

const diff = (prev: PersistedItem, next: PersistedItem): HistoryEntry | null => {
    const [, redoPatches, undoPatches] = produceWithPatches(prev, draft => {
        reconcile(draft, next);
    });
    if (redoPatches.length === 0) return null;
    return { redo: redoPatches, undo: undoPatches };
};

const collectLeafPaths = (value: unknown, prefix: string, into: Set<string>): void => {
    if (prefix && (value === undefined || value === null || typeof value !== 'object')) {
        into.add(prefix);
        return;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            if (prefix) into.add(prefix);
            return;
        }
        value.forEach((item, index) => {
            collectLeafPaths(item, prefix ? `${prefix}.${index}` : String(index), into);
        });
        return;
    }
    if (isPlainObject(value)) {
        const keys = Object.keys(value);
        if (keys.length === 0) {
            if (prefix) into.add(prefix);
            return;
        }
        for (const key of keys) {
            collectLeafPaths(value[key], prefix ? `${prefix}.${key}` : key, into);
        }
        return;
    }
    if (prefix) into.add(prefix);
};

const leafOnly = (paths: Iterable<string>): string[] => {
    const all = [...paths];
    return all
        .filter(path => !all.some(other => other !== path && other.startsWith(`${path}.`)))
        .sort((a, b) => a.split('.').length - b.split('.').length);
};

// Dimensional fields register leaves (`service_dimensional.dim1`). A first
// edit often patches the whole parent object; restore must setValue those
// leaves, never the parent as `''` (that leaves nested Controllers stale).
const restorePathsFromPatches = (
    patches: Patch[],
    before: PersistedItem,
    after: PersistedItem,
): string[] => {
    const paths = new Set<string>();
    for (const patch of patches) {
        if (patch.path.length === 0) continue;
        const dotted = patch.path.map(String).join('.');
        collectLeafPaths(getAtPath(before, dotted), dotted, paths);
        collectLeafPaths(getAtPath(after, dotted), dotted, paths);
        if ('value' in patch) {
            collectLeafPaths(patch.value, dotted, paths);
        }
        if (![...paths].some(path => path === dotted || path.startsWith(`${dotted}.`))) {
            paths.add(dotted);
        }
    }
    const leaves = leafOnly(paths);
    if (leaves.length > 0) return leaves;
    return differingLeafPaths(before, after);
};

const getAtPath = (obj: unknown, dotted: string): unknown => {
    if (!dotted) return obj;
    return dotted.split('.').reduce<unknown>((acc, key) => {
        if (acc === null || acc === undefined || typeof acc !== 'object') return undefined;
        return (acc as Record<string, unknown>)[key];
    }, obj);
};

const differingLeafPaths = (before: PersistedItem, after: PersistedItem): string[] => {
    const paths = new Set<string>();
    collectLeafPaths(before, '', paths);
    collectLeafPaths(after, '', paths);
    return leafOnly(paths).filter(path => !isEqual(getAtPath(before, path), getAtPath(after, path)));
};

const isEmptyValue = (value: unknown): boolean => {
    if (value === undefined || value === null || value === '') return true;
    if (isDate(value)) return false;
    if (Array.isArray(value)) {
        return value.length === 0 || value.every(isEmptyValue);
    }
    if (typeof value === 'object') {
        const keys = Object.keys(value as object);
        return keys.length === 0 || keys.every(key => isEmptyValue((value as Record<string, unknown>)[key]));
    }
    return false;
};

const isEmptyEqualSnapshot = (a: unknown, b: unknown): boolean => {
    if (isEmptyValue(a) && isEmptyValue(b)) return true;
    if (isEqual(a, b)) return true;
    if (isPlainObject(a) && isPlainObject(b)) {
        const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
        for (const key of keys) {
            if (!isEmptyEqualSnapshot(a[key], b[key])) return false;
        }
        return true;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return isEmptyValue(a) && isEmptyValue(b);
        return a.every((item, index) => isEmptyEqualSnapshot(item, b[index]));
    }
    return false;
};

// Layout remounts drop unregistered fields from getValues() without being a
// real user edit. Keep those keys from the last full snapshot. Do not treat
// newly registered empty fields as edits (sparse defaultItem vs gtin: '').
const mergeLiveWithBaseline = (
    previous: PersistedItem | null,
    live: PersistedItem,
): PersistedItem => {
    if (previous === null) return live;
    const merged = cloneSnapshot(previous) as Record<string, unknown>;
    for (const key of Object.keys(live)) {
        const previousValue = merged[key];
        const liveValue = live[key];
        if (isPlainObject(previousValue) && isPlainObject(liveValue)) {
            merged[key] = mergeLiveWithBaseline(
                previousValue as PersistedItem,
                liveValue as PersistedItem,
            );
            continue;
        }
        if (!Object.prototype.hasOwnProperty.call(previous, key) && isEmptyValue(liveValue)) {
            continue;
        }
        merged[key] = cloneSnapshot(liveValue);
    }
    return merged as PersistedItem;
};

// An unnamed watch is a full reset (new item) when overlapping values
// actually changed. New keys (section expand, extra dimensions) and missing
// keys (unmounted fields) are layout remounts — keep history.
const isUnnamedLayoutUnmount = (previous: PersistedItem, live: PersistedItem): boolean => {
    for (const key of Object.keys(live)) {
        if (!Object.prototype.hasOwnProperty.call(previous, key)) {
            continue;
        }
        const previousValue = previous[key];
        const liveValue = live[key];
        if (
            isPlainObject(previousValue) && isPlainObject(liveValue)
        ) {
            if (!isUnnamedLayoutUnmount(previousValue as PersistedItem, liveValue as PersistedItem)) {
                return false;
            }
            continue;
        }
        if (!isEqual(previousValue, liveValue)) {
            return false;
        }
    }
    return true;
};

// RHF marks a dirty field with `true`, nested for objects and arrays.
const dirtyLeafPaths = (node: unknown, prefix = ''): string[] => {
    if (node === true) return prefix ? [prefix] : [];
    if (node === null || typeof node !== 'object') return [];
    return Object.entries(node as Record<string, unknown>)
        .flatMap(([key, child]) => dirtyLeafPaths(child, prefix ? `${prefix}.${key}` : key));
};

export const useUndoRedo = (): UseUndoRedoReturn => {
    const formContext = useFormContext();
    // RHF's `useForm` returns a fresh object every render; capturing the
    // methods directly in deps would invalidate effects (and cancel the
    // pending debounce timer) on every unrelated re-render of the form
    // provider. Stash them in a ref so callbacks always reach the latest
    // instance without triggering effect re-runs.
    const formRef = useRef(formContext);
    formRef.current = formContext;

    // Stacks live in refs - mutating them must NOT cause a re-render of every
    // component that uses this hook on every keystroke.
    const undoStackRef = useRef<HistoryEntry[]>([]);
    const redoStackRef = useRef<HistoryEntry[]>([]);
    const baselineRef = useRef<PersistedItem | null>(null);
    const pristineRef = useRef<PersistedItem | null>(null);
    // Boolean (not counter) because RHF emits an unbounded number of watch
    // events while a single `reset` is in flight; we want to swallow ALL of
    // them for the duration of our own restore call.
    const inSelfResetRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const syncAvailability = useCallback(() => {
        const nextCanUndo = undoStackRef.current.length > 0;
        const nextCanRedo = redoStackRef.current.length > 0;
        setCanUndo(prev => (prev === nextCanUndo ? prev : nextCanUndo));
        setCanRedo(prev => (prev === nextCanRedo ? prev : nextCanRedo));
    }, []);

    const clearStacks = useCallback(() => {
        if (undoStackRef.current.length > 0) undoStackRef.current = [];
        if (redoStackRef.current.length > 0) redoStackRef.current = [];
        syncAvailability();
    }, [syncAvailability]);

    // Flush any pending debounced diff into the undo stack synchronously.
    // Called both by the debounce timer and by undo/redo before they read the
    // stacks - otherwise an undo issued mid-typing-burst would compute against
    // a stale baseline and silently drop the in-flight change.
    const commitPending = useCallback(() => {
        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        const previous = baselineRef.current;
        const live = cloneSnapshot(formRef.current.getValues() as PersistedItem);
        const current = mergeLiveWithBaseline(previous, live);

        if (previous === null) {
            baselineRef.current = current;
            return;
        }

        const entry = diff(previous, current);
        baselineRef.current = current;
        if (entry === null) return;

        const stack = undoStackRef.current;
        stack.push(entry);
        if (stack.length > MAX_STACK_SIZE) {
            stack.splice(0, stack.length - MAX_STACK_SIZE);
        }
        if (redoStackRef.current.length > 0) redoStackRef.current = [];
    }, []);

    useEffect(() => {
        const initial = cloneSnapshot(formRef.current.getValues() as PersistedItem);
        baselineRef.current = initial;
        pristineRef.current = initial;

        const subscription = formRef.current.watch((_values, info) => {
            // Our own undo/redo `restore` triggers `reset`, which can fire
            // multiple watch events synchronously inside one call. Swallow
            // the entire reset window without recording.
            if (inSelfResetRef.current) {
                return;
            }

            // A programmatic reset (e.g. loading a different item via the
            // navigation dropdown) emits with no field name. Treat it as a
            // fresh baseline and drop history that belongs to the old item.
            // Unregister / layout remount also emits with no field name but
            // only drops keys; keep history and fold newly mounted keys into
            // the baseline so the next edit does not undo those layout values.
            if (info.name === undefined) {
                const live = cloneSnapshot(formRef.current.getValues() as PersistedItem);
                const previous = baselineRef.current;
                if (previous !== null && isUnnamedLayoutUnmount(previous, live)) {
                    // A save resets onto the values just stored. The watch runs
                    // before isDirty flips, but default values are already the
                    // saved item. A layout remount keeps the old defaults, so
                    // this does not clear history for a section expand.
                    const defaultsMatchLive = isEmptyEqualSnapshot(
                        formRef.current.control._defaultValues,
                        live,
                    );
                    const savedOverPreviousPristine = defaultsMatchLive
                        && !isEmptyEqualSnapshot(pristineRef.current, live);
                    if (savedOverPreviousPristine) {
                        if (debounceTimerRef.current) {
                            clearTimeout(debounceTimerRef.current);
                            debounceTimerRef.current = null;
                        }
                        baselineRef.current = live;
                        pristineRef.current = live;
                        clearStacks();
                        return;
                    }
                    baselineRef.current = mergeLiveWithBaseline(previous, live);
                    return;
                }
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                    debounceTimerRef.current = null;
                }
                baselineRef.current = live;
                pristineRef.current = live;
                clearStacks();
                return;
            }

            // RHF only sets `info.type` ('change') for Controller/register onChange events;
            // programmatic setValue has no type and must not record an undo entry (LAX-9905).
            if (info.type === undefined) {
                if (debounceTimerRef.current === null) {
                    const live = cloneSnapshot(formRef.current.getValues() as PersistedItem);
                    baselineRef.current = mergeLiveWithBaseline(baselineRef.current, live);
                }
                return;
            }

            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                commitPending();
                syncAvailability();
            }, SNAPSHOT_DEBOUNCE_MS);
        });

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            subscription.unsubscribe();
        };
        // Mount-only on purpose: form methods are accessed via formRef so the
        // subscription is stable for the hook's lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const restore = useCallback((next: PersistedItem, changedPaths: string[]) => {
        const valueForPath = (path: string): unknown => {
            const value = getAtPath(next, path);
            return value === undefined || value === null ? '' : value;
        };

        // A restored path matches the saved item when it empty-equals the
        // pristine snapshot, including a non-empty saved string. Empty and
        // missing are the same value (LAX-10563).
        const matchesPristine = (path: string, value: unknown): boolean => {
            const original = getAtPath(pristineRef.current, path);
            return isEmptyEqualSnapshot(value, original);
        };

        const pathsToRestore = changedPaths.length > 0
            ? changedPaths
            : differingLeafPaths((baselineRef.current ?? next) as PersistedItem, next);

        const shouldDirtyForPath = (path: string): boolean =>
            !matchesPristine(path, valueForPath(path));

        // Apply leaf setValue only. A full form.reset() remounts Dimensional
        // Controllers from defaultValues and can leave the typed value on
        // screen. The keepValues reset below is only for the last undo, when
        // every restored path matches the saved item. Validation may have
        // merged other keys into the snapshot; those must not keep Save
        // enabled (LAX-10563).
        inSelfResetRef.current = true;
        try {
            for (const path of pathsToRestore) {
                formRef.current.setValue(path, valueForPath(path), {
                    shouldDirty: shouldDirtyForPath(path),
                    shouldTouch: false,
                });
            }
            const everyPathMatchesPristine = pathsToRestore.length > 0
                && pathsToRestore.every(path => matchesPristine(path, valueForPath(path)));
            // Clear, reference, collection, group and the AngularJS template
            // write with setValue({ shouldDirty: true }) and no event type, so
            // the history does not record them. Such an edit must keep Save
            // enabled when the last recorded step is undone.
            const restoredPaths = new Set(pathsToRestore);
            const unrecordedEditRemains = dirtyLeafPaths(formRef.current.control._formState.dirtyFields)
                .some(path => !restoredPaths.has(path)
                    && !matchesPristine(path, formRef.current.getValues(path)));
            const restoredToPristine = undoStackRef.current.length === 0
                && !unrecordedEditRemains
                && (
                    isEmptyEqualSnapshot(pristineRef.current, next)
                    || everyPathMatchesPristine
                );
            if (restoredToPristine) {
                const defaults = (formRef.current.formState.defaultValues ?? {}) as PersistedItem;
                formRef.current.reset(defaults, {
                    keepValues: true,
                    keepDefaultValues: true,
                    keepDirty: false,
                    keepTouched: false,
                });
            }
        } finally {
            inSelfResetRef.current = false;
        }

        // Named setValue so useValidations' watch (which ignores events during
        // inSelfReset) re-queues. type is undefined, so this hook will not
        // record a new undo step (LAX-9905). shouldDirty stays false when the
        // value matches the saved item, so this write does not turn Save back on.
        for (const path of pathsToRestore) {
            const value = valueForPath(path);
            formRef.current.setValue(path, value, {
                shouldDirty: shouldDirtyForPath(path),
                shouldTouch: false,
            });
        }
        baselineRef.current = cloneSnapshot(next);
    }, []);

    const undo = useCallback((e?: MouseEvent) => {
        e?.preventDefault?.();
        commitPending();
        const entry = undoStackRef.current.pop();
        if (entry === undefined) {
            syncAvailability();
            return;
        }

        const current = baselineRef.current
            ?? cloneSnapshot(formRef.current.getValues() as PersistedItem);
        const next = applyPatches(current, entry.undo);
        redoStackRef.current.push(entry);
        restore(next, restorePathsFromPatches(entry.undo, current, next));
        syncAvailability();
    }, [commitPending, restore, syncAvailability]);

    const redo = useCallback((e?: MouseEvent) => {
        e?.preventDefault?.();
        commitPending();
        const entry = redoStackRef.current.pop();
        if (entry === undefined) {
            syncAvailability();
            return;
        }

        const current = baselineRef.current
            ?? cloneSnapshot(formRef.current.getValues() as PersistedItem);
        const next = applyPatches(current, entry.redo);
        undoStackRef.current.push(entry);
        restore(next, restorePathsFromPatches(entry.redo, current, next));
        syncAvailability();
    }, [commitPending, restore, syncAvailability]);

    return { undo, redo, canUndo, canRedo };
};
